/**
 * Lightweight, code-drawn previews of real GrabNChat dashboard screens
 * (Broadcast, Flow builder, Inbox, Contacts, Drip campaigns, Dashboard,
 * Channels). Pure HTML/CSS — no image downloads — so they're crisp, fast and
 * accessible (each has role="img" + a descriptive aria-label, the equivalent
 * of alt text). All names and numbers are illustrative sample data.
 */
import {
  Users, FileText, Clock, Send, CheckCheck, Eye, AlertCircle, MessageSquare, GitBranch, UserCheck, Tag,
  Bot, Instagram, Search, Phone, Timer, LogOut, Play, BadgeCheck, KeyRound, Target, BarChart3, ShieldCheck,
} from "lucide-react";

function Frame({ label, title, children, className = "" }) {
  return (
    <figure role="img" aria-label={label} className={`relative w-full min-w-0 ${className}`}>
      <div className="absolute -inset-4 bg-[#25D366]/10 blur-3xl rounded-3xl" aria-hidden />
      <div className="relative bg-white border border-slate-200 rounded-3xl shadow-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
          <span className="flex gap-1.5" aria-hidden><i className="w-2.5 h-2.5 rounded-full bg-rose-300" /><i className="w-2.5 h-2.5 rounded-full bg-amber-300" /><i className="w-2.5 h-2.5 rounded-full bg-emerald-300" /></span>
          <span className="ml-2 text-[11px] font-medium text-slate-500 truncate">GrabNChat · {title}</span>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
      <figcaption className="sr-only">{label}</figcaption>
    </figure>
  );
}

const Chip = ({ children, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-[#25D366]/10 text-[#128C4A]",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>{children}</span>;
};

/* ------------------------------------------------------------ Campaigns */
export function CampaignMockup() {
  const steps = ["Audience", "Template", "Personalise", "Schedule"];
  const stats = [["Sent", "2,480", Send, 100], ["Delivered", "2,431", CheckCheck, 98], ["Read", "2,102", Eye, 85], ["Failed", "49", AlertCircle, 2]];
  return (
    <Frame title="Broadcast" label="GrabNChat WhatsApp broadcast campaign builder showing audience, approved template, personalisation, schedule and delivery results">
      <div className="flex items-center justify-between gap-2">
        <div className="font-display font-semibold text-slate-900 text-sm">Diwali offer – broadcast</div>
        <Chip tone="green">Completed</Chip>
      </div>
      <ol className="mt-3 flex flex-wrap gap-1.5">
        {steps.map((s, i) => (
          <li key={s} className="flex items-center gap-1 text-[11px] font-semibold text-slate-600">
            <span className="w-4 h-4 rounded-full bg-[#25D366] text-white grid place-items-center text-[9px]">{i + 1}</span>{s}
          </li>
        ))}
      </ol>
      <dl className="mt-4 grid sm:grid-cols-2 gap-2 text-xs">
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><dt className="flex items-center gap-1 text-slate-500"><Users className="w-3.5 h-3.5" /> Audience</dt><dd className="mt-1 font-semibold text-slate-900">Tag: vip-customers · 2,480 contacts</dd></div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><dt className="flex items-center gap-1 text-slate-500"><FileText className="w-3.5 h-3.5" /> Template</dt><dd className="mt-1 font-semibold text-slate-900">diwali_offer <Chip tone="green">Approved</Chip></dd></div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><dt className="text-slate-500">Variables</dt><dd className="mt-1 font-semibold text-slate-900">{"{{1}}"} → First name · {"{{2}}"} → Coupon</dd></div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><dt className="flex items-center gap-1 text-slate-500"><Clock className="w-3.5 h-3.5" /> Schedule</dt><dd className="mt-1 font-semibold text-slate-900">Sat, 10:00 AM</dd></div>
      </dl>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {stats.map(([k, v, I, pct]) => (
          <div key={k} className="rounded-xl border border-slate-100 p-2.5">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><I className="w-3 h-3" /> {k}</div>
            <div className="mt-0.5 font-display text-lg font-bold text-slate-900">{v}</div>
            <div className="mt-1 h-1 rounded-full bg-slate-100"><div className={`h-1 rounded-full ${k === "Failed" ? "bg-rose-400" : "bg-[#25D366]"}`} style={{ width: `${pct}%` }} /></div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

/* -------------------------------------------------------------- Chatbot */
export function ChatbotMockup() {
  const nodes = [
    { icon: MessageSquare, t: "Trigger", d: "Customer sends “price”", tone: "bg-sky-50 border-sky-200" },
    { icon: Bot, t: "Bot reply + buttons", d: "Plans · Book a demo · Talk to us", tone: "bg-white border-slate-200" },
    { icon: GitBranch, t: "Question", d: "What's your business name & city?", tone: "bg-white border-slate-200" },
    { icon: Target, t: "Save lead", d: "Adds contact to Leads, tag: website", tone: "bg-violet-50 border-violet-200" },
    { icon: UserCheck, t: "Agent handoff", d: "Assign to Sales team in Inbox", tone: "bg-[#25D366]/10 border-[#25D366]/30" },
  ];
  return (
    <Frame title="Flow builder" label="GrabNChat no-code WhatsApp chatbot flow: keyword trigger, bot reply with buttons, question, lead capture and handoff to a human agent">
      <div className="flex items-center justify-between">
        <div className="font-display font-semibold text-slate-900 text-sm">Lead qualification bot</div>
        <Chip tone="green">Published</Chip>
      </div>
      <ol className="mt-4 space-y-0">
        {nodes.map((n, i) => (
          <li key={n.t} className="flex flex-col items-center">
            <div className={`w-full max-w-xs rounded-xl border px-3 py-2 flex items-start gap-2.5 ${n.tone}`}>
              <n.icon className="w-4 h-4 mt-0.5 text-slate-700 shrink-0" />
              <div className="min-w-0"><div className="text-xs font-semibold text-slate-900">{n.t}</div><div className="text-[11px] text-slate-600">{n.d}</div></div>
            </div>
            {i < nodes.length - 1 && <span className="w-px h-3 bg-slate-300" aria-hidden />}
          </li>
        ))}
      </ol>
    </Frame>
  );
}

/* --------------------------------------------------------- Shared inbox */
export function InboxMockup() {
  const convos = [
    { n: "Priya Sharma", m: "Is the Growth plan annual?", a: "RK", ch: "wa", u: 2, active: true },
    { n: "Rahul Verma", m: "Thanks, order received 👍", a: "AS", ch: "wa" },
    { n: "@meera.designs", m: "Do you ship to Pune?", a: "RK", ch: "ig", u: 1 },
    { n: "Aman Gupta", m: "Can I book a demo?", a: "—", ch: "wa" },
  ];
  return (
    <Frame title="Inbox" label="GrabNChat shared team inbox with WhatsApp and Instagram conversations, agent assignment, tags and contact details on one business number">
      <div className="grid grid-cols-12 gap-3 text-xs">
        <div className="col-span-12 sm:col-span-5 space-y-1.5">
          <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1.5 text-slate-400"><Search className="w-3.5 h-3.5" /> Search chats</div>
          {convos.map((c) => (
            <div key={c.n} className={`rounded-lg px-2 py-1.5 flex items-center gap-2 ${c.active ? "bg-[#25D366]/10" : ""}`}>
              <span className={`w-6 h-6 rounded-full grid place-items-center text-white shrink-0 ${c.ch === "ig" ? "bg-pink-500" : "bg-[#25D366]"}`}>{c.ch === "ig" ? <Instagram className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}</span>
              <div className="min-w-0 flex-1"><div className="font-semibold text-slate-900 truncate">{c.n}</div><div className="text-[11px] text-slate-500 truncate">{c.m}</div></div>
              <span className="text-[10px] font-bold text-slate-500">{c.a}</span>
              {c.u && <span className="w-4 h-4 rounded-full bg-[#25D366] text-white grid place-items-center text-[9px] font-bold">{c.u}</span>}
            </div>
          ))}
        </div>
        <div className="col-span-12 sm:col-span-7 rounded-xl bg-[#F0F2F5] p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-900">Priya Sharma</span>
            <span className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600"><UserCheck className="w-3 h-3" /> Assigned: Rohit K.</span>
          </div>
          <div className="flex gap-1"><Chip tone="violet"><Tag className="w-3 h-3" />lead</Chip><Chip tone="amber">pricing</Chip></div>
          <div className="self-start max-w-[85%] rounded-xl bg-white px-2.5 py-1.5 text-slate-800 shadow-sm">Hi! Is the Growth plan billed yearly?</div>
          <div className="self-end max-w-[85%] rounded-xl bg-[#DCF8C6] px-2.5 py-1.5 text-slate-800 shadow-sm">Yes — ₹19,999 for a full year. Want a quick demo?</div>
          <div className="self-start max-w-[85%] rounded-xl bg-white px-2.5 py-1.5 text-slate-800 shadow-sm">Sure, tomorrow 11 AM works.</div>
        </div>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ CRM */
export function CrmMockup() {
  const timeline = [
    ["Lead captured", "Facebook Lead Ad · Home loans form", "bg-violet-400"],
    ["Template sent", "welcome_offer · delivered, read", "bg-sky-400"],
    ["Customer replied", "“Please share the EMI details”", "bg-[#25D366]"],
    ["Stage updated", "New → Qualified · Assigned to Neha", "bg-amber-400"],
  ];
  return (
    <Frame title="Contacts" label="GrabNChat WhatsApp CRM contact profile with tags, custom attributes and a timeline from lead to follow-up">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center text-sm font-bold">AK</span>
        <div className="min-w-0"><div className="font-display font-semibold text-slate-900 text-sm">Arjun Kapoor</div><div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> +91 98xxx xx210</div></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1"><Chip tone="violet">home-loan</Chip><Chip tone="green">qualified</Chip><Chip>delhi-ncr</Chip></div>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
        {[["City", "Gurugram"], ["Budget", "₹75L"], ["Source", "Lead Ads"]].map(([k, v]) => (
          <div key={k} className="rounded-lg bg-slate-50 border border-slate-100 p-2"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-slate-900">{v}</dd></div>
        ))}
      </dl>
      <ol className="mt-4 space-y-2.5 border-l border-slate-200 ml-1.5">
        {timeline.map(([t, d, c]) => (
          <li key={t} className="relative pl-4 text-xs">
            <span className={`absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full ${c}`} aria-hidden />
            <div className="font-semibold text-slate-900">{t}</div><div className="text-slate-500">{d}</div>
          </li>
        ))}
      </ol>
    </Frame>
  );
}

/* ----------------------------------------------------------- Automation */
export function AutomationMockup() {
  const steps = [
    { i: Play, t: "Entry", d: "Contact gets tag “new-lead”", k: "trigger" },
    { i: FileText, t: "Send template", d: "welcome_offer" },
    { i: Timer, t: "Wait 2 days", d: "Send 10 AM – 7 PM, Mon–Sat" },
    { i: FileText, t: "Follow-up", d: "reminder_with_coupon" },
    { i: Timer, t: "Wait 3 days", d: "" },
    { i: LogOut, t: "Exit template", d: "order_confirmed → contact leaves drip", k: "exit" },
  ];
  return (
    <Frame title="Drip campaigns" label="GrabNChat WhatsApp automation: drip campaign with entry trigger, template messages, delays, follow-up and automatic exit on conversion">
      <div className="flex items-center justify-between">
        <div className="font-display font-semibold text-slate-900 text-sm">New lead nurture</div>
        <Chip tone="green">Running</Chip>
      </div>
      <div className="mt-2 flex gap-3 text-[11px] text-slate-500"><span><b className="text-slate-900">412</b> enrolled</span><span><b className="text-slate-900">96</b> converted</span><span><b className="text-slate-900">0</b> duplicates</span></div>
      <ol className="mt-4 space-y-1.5">
        {steps.map((s) => (
          <li key={s.t + s.d} className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-xs ${s.k === "trigger" ? "border-sky-200 bg-sky-50" : s.k === "exit" ? "border-[#25D366]/30 bg-[#25D366]/10" : "border-slate-200 bg-white"}`}>
            <s.i className="w-4 h-4 text-slate-700 shrink-0" />
            <span className="font-semibold text-slate-900">{s.t}</span>
            {s.d && <span className="text-slate-500 truncate">· {s.d}</span>}
          </li>
        ))}
      </ol>
    </Frame>
  );
}

/* ------------------------------------------------------------ Analytics */
export function AnalyticsMockup() {
  const days = [30, 42, 28, 55, 48, 62, 40, 71, 58, 66, 52, 80, 74, 69];
  const kpis = [["Messages sent", "12,480"], ["Delivered", "98.1%"], ["Read", "86.4%"], ["Replies", "1,932"]];
  const status = [["Read", 86, "bg-[#25D366]"], ["Delivered", 12, "bg-sky-400"], ["Failed", 2, "bg-rose-400"]];
  return (
    <Frame title="Dashboard" label="GrabNChat WhatsApp analytics dashboard with messages sent, delivery and read rates, replies, daily message volume and top templates">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {kpis.map(([k, v]) => (
          <div key={k} className="rounded-xl border border-slate-100 bg-slate-50 p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="font-display text-lg font-bold text-slate-900">{v}</div></div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-700"><BarChart3 className="w-3.5 h-3.5" /> Messages per day</div>
      <div className="mt-2 h-24 flex items-end gap-1" aria-hidden>
        {days.map((h, i) => <div key={i} className="flex-1 rounded-t bg-[#25D366]/80" style={{ height: `${h}%` }} />)}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
        <div>
          <div className="font-semibold text-slate-700">Delivery status</div>
          <div className="mt-2 flex h-2.5 rounded-full overflow-hidden">{status.map(([k, v, c]) => <div key={k} className={c} style={{ width: `${v}%` }} />)}</div>
          <div className="mt-1.5 flex flex-wrap gap-2 text-[11px] text-slate-500">{status.map(([k, v]) => <span key={k}>{k} {v}%</span>)}</div>
        </div>
        <div>
          <div className="font-semibold text-slate-700">Top templates</div>
          <ul className="mt-1.5 space-y-1 text-[11px] text-slate-600">
            <li className="flex justify-between"><span>order_update</span><b className="text-slate-900">4,110</b></li>
            <li className="flex justify-between"><span>diwali_offer</span><b className="text-slate-900">2,480</b></li>
            <li className="flex justify-between"><span>payment_reminder</span><b className="text-slate-900">1,206</b></li>
          </ul>
        </div>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------ Channels / API */
export function ConnectMockup() {
  return (
    <Frame title="Channels" label="GrabNChat channels screen showing a WhatsApp Business API number connected through Meta Embedded Signup, quality rating, approved templates and an API key">
      <div className="rounded-2xl border border-[#25D366]/30 bg-[#25D366]/5 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-[#25D366] text-white grid place-items-center"><MessageSquare className="w-4 h-4" /></span>
            <div><div className="font-semibold text-slate-900 text-sm flex items-center gap-1">Your Business <BadgeCheck className="w-4 h-4 text-[#25D366]" /></div><div className="text-xs text-slate-500">+91 93xxx xx747 · WhatsApp Business API</div></div>
          </div>
          <Chip tone="green">Connected</Chip>
        </div>
        <dl className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-lg bg-white border border-slate-100 p-2"><dt className="text-slate-500">Quality</dt><dd className="font-semibold text-emerald-600">High</dd></div>
          <div className="rounded-lg bg-white border border-slate-100 p-2"><dt className="text-slate-500">Templates</dt><dd className="font-semibold text-slate-900">12 approved</dd></div>
          <div className="rounded-lg bg-white border border-slate-100 p-2"><dt className="text-slate-500">Signup</dt><dd className="font-semibold text-slate-900">Embedded</dd></div>
        </dl>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
        <div className="rounded-xl border border-slate-200 p-3 flex items-center gap-2"><Instagram className="w-4 h-4 text-pink-500" /> Instagram <span className="ml-auto"><Chip tone="green">Connected</Chip></span></div>
        <div className="rounded-xl border border-slate-200 p-3 flex items-center gap-2"><KeyRound className="w-4 h-4 text-slate-700" /> API key <code className="ml-auto text-[10px] text-slate-500">gnc_live_a1b2…</code></div>
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="w-3.5 h-3.5 text-[#25D366]" /> Official WhatsApp Cloud API via Meta Tech Provider</div>
    </Frame>
  );
}

export const MOCKUPS = {
  campaign: CampaignMockup,
  chatbot: ChatbotMockup,
  inbox: InboxMockup,
  crm: CrmMockup,
  automation: AutomationMockup,
  analytics: AnalyticsMockup,
  connect: ConnectMockup,
};
